'use client';

import React, { useEffect, useState } from 'react';

import { preload } from 'react-dom';

import { useTranslations } from 'next-intl';

import { QuizQuestion } from '../QuizQuestion';
import { QuizSignupGate } from '../QuizSignupGate';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { NavigableStepProgressBar } from '@/components/NavigableStepProgressBar';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import {
  DEFAULT_QUIZ_ANSWERS,
  STEP_TO_ANSWER_KEY,
  TOTAL_STEPS,
} from '@/modules/quiz/constants';
import {
  type CategoryValue,
  type ColorValue,
  type QuizAnswers,
  QuizStep,
  type StyleValue,
} from '@/modules/quiz/types';
import { getOptionsForStep, getResultsURL } from '@/modules/quiz/utils';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type QuizContainerProps = {
  initialSelection?: CategoryValue | undefined;
};

export function QuizContainer({ initialSelection }: QuizContainerProps) {
  const [{ web_9149_style_quiz_signup_gate }, bucket] = useExperiments(
    ['web_9149_style_quiz_signup_gate'],
    { deferred: true }
  );
  const t = useTranslations('quiz');
  const [showInterstitialSignup, setShowInterstitialSignup] = useState(false);
  const router = useExtendedRouter();
  const [answers, setAnswers] = useState<QuizAnswers>(() => ({
    ...DEFAULT_QUIZ_ANSWERS,
    ...(initialSelection && { category: initialSelection }),
  }));
  const [currentStep, setCurrentStep] = useState<QuizStep>(
    () => QuizStep.Category
  );
  const [showError, setShowError] = useState(false);
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  function preloadImages(nextAnswers: QuizAnswers, step: QuizStep) {
    const options = getOptionsForStep(step, nextAnswers);
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches;
    for (const option of options) {
      const src =
        isMobile && option.imageSrcMobile
          ? option.imageSrcMobile
          : option.imageSrc;
      if (src) {
        preload(src, { as: 'image' });
      }
    }
  }

  useEffect(() => {
    if (answers.category) {
      preloadImages(answers, QuizStep.Style);
    }
  }, [answers.category]);

  const options = getOptionsForStep(currentStep, answers);
  const currentSelection = answers[STEP_TO_ANSWER_KEY[currentStep]];

  function handleSelect(value: string) {
    setShowError(false);

    let nextAnswers = answers;
    switch (currentStep) {
      case QuizStep.Category:
        nextAnswers = {
          ...answers,
          category: value as CategoryValue,
          style: null,
        };
        break;
      case QuizStep.Style:
        nextAnswers = {
          ...answers,
          style: value as StyleValue,
        };
        break;
      case QuizStep.Colors:
        nextAnswers = {
          ...answers,
          colors: value as ColorValue,
        };
        break;
    }
    setAnswers(nextAnswers);
  }

  function handleContinue(e: React.MouseEvent<HTMLButtonElement>) {
    if (currentSelection === null) {
      setShowError(true);
      return;
    }

    // Q1 and Q2
    if (currentStep !== QuizStep.Colors) {
      e.currentTarget?.blur();
      setCurrentStep((prev) => (prev + 1) as QuizStep);
      setShowError(false);
    } else {
      const finalAnswers = { ...answers, finishedQuiz: true };
      sendActivityTrackerEvent(ActivityTrackerEventType.STYLE_QUIZ_ACTION, {
        type: 'quiz_complete',
        category: finalAnswers.category,
        style: finalAnswers.style,
        colours: finalAnswers.colors,
        schemaVersion: '1.0',
      });

      bucket();
      if (web_9149_style_quiz_signup_gate === VARIANT_IDENTIFIER) {
        setShowInterstitialSignup(true);
        setAnswers(finalAnswers);
      } else {
        const resultsURL = getResultsURL(finalAnswers);
        router.push(resultsURL);
      }
    }
  }

  function handleBack() {
    const isFirstStep = currentStep === QuizStep.Category;
    if (!isFirstStep) {
      setCurrentStep((prev) => (prev - 1) as QuizStep);
      setShowError(false);
    }
  }

  return (
    <div className={styles.container}>
      {showInterstitialSignup ? (
        <QuizSignupGate answers={answers} />
      ) : (
        <>
          <div className={styles.header}>
            <div className={styles.headerNavigation}>
              <NavigableStepProgressBar
                currentStep={currentStep}
                totalSteps={TOTAL_STEPS}
                onBack={
                  currentStep === QuizStep.Category ? undefined : handleBack
                }
                isBackDisabled={currentStep === QuizStep.Category}
                isNextDisabled={showError || currentStep === QuizStep.Colors}
              />
            </div>
          </div>

          <QuizQuestion
            title={t(`FindYourStyle.Q${currentStep}.Title`)}
            options={options}
            selectedValue={currentSelection}
            onSelect={handleSelect}
            error={
              showError ? t('FindYourStyle.Error.SelectOption') : undefined
            }
            alignment={currentStep === QuizStep.Category ? 'ladder' : 'grid'}
            name={`quiz-step-${currentStep}`}
          />

          <div className={styles.continueWrap}>
            <ButtonExperiment onClick={handleContinue} disabled={showError}>
              {currentStep === QuizStep.Colors
                ? t('FindYourStyle.Finish')
                : t('FindYourStyle.Next')}
            </ButtonExperiment>
          </div>
        </>
      )}
    </div>
  );
}
